import { useEffect, useState } from "react";
import { Briefcase, Check, ChevronDown, Download, Inbox, Linkedin, Loader2, Mail, MapPin, Minus, Phone, Plus, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Card, Empty, btn } from "@/components/common";
import { cn } from "@/lib/utils";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";
const lbl = "mb-1.5 block text-xs font-medium text-slate-500";
const STATUSES = ["new", "reviewing", "interview", "offer", "hired", "rejected"];
const QUALS = [
  ["computerUse", "Computer, email & online tools", true],
  ["membershipFee", "Willing to pay $32/mo membership", true],
  ["salesHospitality", "Sales / hospitality experience", false],
  ["customerService", "Customer service experience", false],
  ["remoteWork", "Remote / flexible-schedule experience", false],
];
const STATUS_META = {
  new: ["New", "bg-sky-50 text-sky-700"],
  reviewing: ["Reviewing", "bg-amber-50 text-amber-700"],
  interview: ["Interview", "bg-violet-50 text-violet-700"],
  offer: ["Offer", "bg-indigo-50 text-indigo-700"],
  hired: ["Hired", "bg-emerald-50 text-emerald-700"],
  rejected: ["Rejected", "bg-slate-100 text-slate-500"],
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };
const RESUME_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/talent/applications`;

const JOB_TEMPLATES = [
  {
    key: "ita",
    label: "Independent Travel Agent",
    title: "Independent Travel Agent",
    location: "Remote (US)",
    type: "Independent Contractor · Flexible",
    description: `Topsail Travel, headquartered in Oklahoma City with additional offices in Virginia, Florida, and Texas, is a growing travel agency focused on helping clients create unforgettable vacations and experiences around the world. From relaxing beach getaways to adventurous international trips, our team works to make travel planning simple, exciting, and stress-free.

At Topsail Travel, we believe travel should be accessible, rewarding, and fun — not only for our clients, but for our agents too. We are passionate about helping new advisors learn the industry, grow their confidence, and build a flexible career in travel.

About the role
This is a remote Independent Travel Agent opportunity with flexible scheduling, allowing you to work from anywhere and create your own schedule. No prior travel industry experience is required — we provide training, tools, and ongoing support to help you get started.

Travel Agents assist clients with planning and booking vacations, cruises, hotels, flights, rental cars, and activities while providing friendly customer service throughout the process. Agents also learn how to work with travel suppliers, find great vacation options for clients, and grow their own client base over time.

This opportunity is ideal for someone who enjoys helping people, loves travel, and is looking for a flexible way to earn income while learning and growing in the travel industry.

There is also a standard agency membership fee of $32/month, which includes training, access to booking tools and suppliers, a branded email address, and insurance coverage.

What we're looking for
• Passion for travel and helping others
• Strong communication and customer service skills
• Organized and detail-oriented
• Comfortable using computers, email, and online tools
• Self-motivated with a willingness to learn
• Sales or hospitality experience is helpful, but not required
• No prior travel industry experience necessary — training provided
• Ability to work independently and manage your own schedule`,
  },
  {
    key: "ta-admin",
    label: "Travel Agent & Admin Assistant",
    title: "Travel Agent & Administrative Assistant",
    location: "Remote (US)",
    type: "Independent Contractor · Flexible",
    description: `Topsail Travel, headquartered in Oklahoma City with additional offices in Virginia, Florida, and Texas, is a growing travel agency focused on helping clients create unforgettable vacations and experiences around the world. From relaxing beach getaways to adventurous international trips, our team works to make travel planning simple, exciting, and stress-free.

At Topsail Travel, we believe travel should be accessible, rewarding, and fun — not only for our clients, but for our agents too. We are passionate about helping new advisors learn the industry, grow their confidence, and build a flexible career in travel while supporting the day-to-day operations of our agency.

About the role
This is a remote Independent Travel Agent & Administrative Assistant opportunity with flexible scheduling, allowing you to work from anywhere while creating your own travel business. No prior travel industry experience is required — we provide training, tools, and ongoing support to help you get started.

In addition to independently planning and booking travel for your own clients, this position includes administrative responsibilities to help support the agency. Duties may include answering incoming phone calls, monitoring and responding to general agency emails, assisting with client inquiries, directing requests to the appropriate team members, and helping with general office and administrative tasks.

Travel Agents assist clients with planning and booking vacations, cruises, hotels, flights, rental cars, and activities while providing friendly customer service throughout the process. Agents also learn how to work with travel suppliers, find great vacation options for clients, and grow their own client base over time.

This opportunity is ideal for someone who enjoys helping people, loves travel, has strong organizational skills, and is looking for a flexible way to earn income while learning and growing in the travel industry.

There is also a standard agency membership fee of $32/month, which includes training, access to booking tools and suppliers, a branded email address, and insurance coverage.

What we're looking for
• Passion for travel and helping others
• Strong communication and customer service skills
• Comfortable answering phone calls and responding to emails
• Organized, detail-oriented, and able to multitask
• Comfortable using computers, email, and online tools
• Self-motivated with a willingness to learn
• Ability to work independently and manage your own schedule
• Sales, hospitality, or administrative experience is helpful, but not required
• No prior travel industry experience necessary — training provided`,
  },
];

function PostingDialog({ open, onOpenChange, initial, onSaved }) {
  const [v, setV] = useState({ title: "", location: "", type: "Full-time", description: "", status: "open" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setV({ title: initial?.title || "", location: initial?.location || "", type: initial?.type || "Full-time", description: initial?.description || "", status: initial?.status || "open" }); }, [open, initial]);
  const save = async () => {
    if (!v.title.trim()) return toast.error("Add a job title");
    setBusy(true);
    try {
      if (initial) await api.patch(`/talent/postings/${initial.id}`, v);
      else await api.post("/talent/postings", v);
      toast.success(initial ? "Opening updated" : "Opening posted");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="posting-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-navy-900">{initial ? "Edit opening" : "New opening"}</DialogTitle><DialogDescription>Open roles appear on your public Careers page.</DialogDescription></DialogHeader>
        {!initial && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="posting-templates">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Start from a template</p>
            <div className="flex flex-wrap gap-2">
              {JOB_TEMPLATES.map((t) => (
                <button key={t.key} type="button" onClick={() => setV({ title: t.title, location: t.location, type: t.type, description: t.description, status: "open" })} data-testid={`posting-template-${t.key}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-navy-900 hover:border-navy-400 hover:bg-navy-50">
                  <Briefcase className="h-3.5 w-3.5 text-[#C9A96E]" />{t.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid gap-3">
          <label><span className={lbl}>Job title *</span><input className={field} value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="posting-title" /></label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label><span className={lbl}>Location</span><input className={field} value={v.location} onChange={(e) => setV({ ...v, location: e.target.value })} placeholder="Remote" data-testid="posting-location" /></label>
            <label><span className={lbl}>Type</span><input className={field} value={v.type} onChange={(e) => setV({ ...v, type: e.target.value })} placeholder="Full-time" data-testid="posting-type" /></label>
          </div>
          <label><span className={lbl}>Description</span><textarea rows={5} className={cn(field, "h-auto py-2.5")} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="posting-description" /></label>
          <label className="flex items-center gap-2 text-sm text-navy-900"><input type="checkbox" checked={v.status === "open"} onChange={(e) => setV({ ...v, status: e.target.checked ? "open" : "closed" })} data-testid="posting-open" />Show on Careers page (open)</label>
        </div>
        <div className="flex justify-end gap-2"><button onClick={() => onOpenChange(false)} className={btn.secondary}>Cancel</button><button disabled={busy} onClick={save} className={btn.primary} data-testid="posting-save">{busy ? "Saving…" : "Save opening"}</button></div>
      </DialogContent>
    </Dialog>
  );
}

function AppCard({ a, onChange }) {
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATUS_META[a.status] || STATUS_META.new;
  const setStatus = async (status) => { setBusy(true); try { await api.patch(`/talent/applications/${a.id}`, { status }); await onChange(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const addNote = async () => { if (!note.trim()) return; setBusy(true); try { await api.patch(`/talent/applications/${a.id}`, { note }); setNote(""); await onChange(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const remove = async () => { if (!window.confirm("Delete this application? This cannot be undone.")) return; try { await api.delete(`/talent/applications/${a.id}`); toast.success("Application deleted"); await onChange(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <Card className="p-4 sm:p-5" data-testid={`application-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-navy-900">{a.name}</span>
            <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", cls)}>{label}</span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">{a.postingTitle} · applied {fmt(a.createdAt)}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-navy-900"><Mail className="h-3.5 w-3.5" />{a.email}</a>
            {a.phone && <a href={`tel:${a.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1 hover:text-navy-900"><Phone className="h-3.5 w-3.5" />{a.phone}</a>}
            {a.yearsExperience && <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{a.yearsExperience} yrs</span>}
            {a.linkedin && <a href={a.linkedin.startsWith("http") ? a.linkedin : `https://${a.linkedin}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-navy-900"><Linkedin className="h-3.5 w-3.5" />Profile</a>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {a.resumeKey && <a href={`${RESUME_BASE}/${a.id}/resume`} target="_blank" rel="noopener noreferrer" data-testid={`application-resume-${a.id}`} className={`${btn.secondary} h-8`}><Download className="h-3.5 w-3.5" />Resume</a>}
          <button onClick={remove} data-testid={`application-delete-${a.id}`} aria-label="Delete application" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {a.message && <p className="mt-3 whitespace-pre-line rounded-xl bg-slate-50 p-3 text-sm text-slate-700">{a.message}</p>}
      {a.qualifications && Object.keys(a.qualifications).length > 0 && (
        <div className="mt-3" data-testid={`application-quals-${a.id}`}>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Qualifications</p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {QUALS.map(([k, label, req]) => {
              const val = a.qualifications[k];
              const Icon = val === true ? Check : val === false ? X : Minus;
              const tone = val === true ? "text-emerald-600" : val === false ? "text-red-600" : "text-slate-300";
              return <div key={k} className="flex items-center gap-1.5 text-xs text-slate-600" data-testid={`application-qual-${a.id}-${k}`}><Icon className={cn("h-3.5 w-3.5 shrink-0", tone)} /><span>{label}{req && <span className="text-slate-400"> (required)</span>}</span></div>;
            })}
          </div>
          {a.autoRejected && <p className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700" data-testid={`application-autoreject-${a.id}`}>Auto-rejected — did not meet a required qualification.</p>}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`application-pipeline-${a.id}`}>
        {STATUSES.map((s) => {
          const [l, c] = STATUS_META[s];
          const active = a.status === s;
          return <button key={s} disabled={busy || active} onClick={() => setStatus(s)} data-testid={`application-status-${a.id}-${s}`} className={cn("rounded-full px-2.5 py-1 text-xs font-medium transition-colors", active ? c + " ring-1 ring-inset ring-current" : "bg-white text-slate-400 ring-1 ring-slate-200 hover:text-navy-900 hover:ring-slate-300")}>{active && <Check className="mr-1 inline h-3 w-3" />}{l}</button>;
        })}
      </div>
      <button onClick={() => setOpen((o) => !o)} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-navy-900" data-testid={`application-notes-toggle-${a.id}`}>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />Notes{a.notes?.length ? ` (${a.notes.length})` : ""}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {(a.notes || []).map((n, i) => <div key={i} className="rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600"><span className="whitespace-pre-line text-navy-900">{n.text}</span><span className="mt-1 block text-slate-400">{n.by} · {fmt(n.at)}</span></div>)}
          <div className="flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a private note…" className={cn(field, "h-9")} data-testid={`application-note-input-${a.id}`} />
            <button disabled={busy || !note.trim()} onClick={addNote} className={`${btn.secondary} h-9`} data-testid={`application-note-add-${a.id}`}>Add</button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function TalentTab() {
  const [postings, setPostings] = useState(null);
  const [apps, setApps] = useState(null);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("new");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPostings = () => api.get("/talent/postings").then(({ data }) => setPostings(data.items)).catch(() => setPostings([]));
  const loadApps = () => api.get("/talent/applications").then(({ data }) => { setApps(data.items); setStats(data.stats); if ((data.items || []).some((a) => !a.seenByAdmin)) api.post("/talent/applications/seen").catch(() => {}); }).catch(() => { setApps([]); setStats({}); });
  useEffect(() => { loadPostings(); loadApps(); }, []);

  const togglePosting = async (p) => { try { await api.patch(`/talent/postings/${p.id}`, { ...p, status: p.status === "open" ? "closed" : "open" }); await loadPostings(); } catch (e) { toast.error(errMsg(e)); } };
  const removePosting = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; try { await api.delete(`/talent/postings/${p.id}`); await loadPostings(); } catch (e) { toast.error(errMsg(e)); } };

  const shown = (apps || []).filter((a) => filter === "all" || a.status === filter);

  return (
    <div className="space-y-6" data-testid="agents-talent-page">
      {/* Job openings */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-navy-900"><Briefcase className="h-4 w-4 text-[#C9A96E]" />Job openings</h3>
          <button onClick={() => { setEditing(null); setDlg(true); }} className={btn.primary + " h-9"} data-testid="talent-new-posting"><Plus className="h-4 w-4" />New opening</button>
        </div>
        {postings === null ? (
          <Card><div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-navy-700" /></div></Card>
        ) : postings.length === 0 ? (
          <Card className="p-5"><p className="text-sm text-slate-500" data-testid="talent-no-postings">No openings yet. Post one to list it on your public Careers page — a general application is always available to candidates.</p></Card>
        ) : (
          <div className="space-y-2.5">
            {postings.map((p) => (
              <Card key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`talent-posting-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-navy-900">{p.title}</p>
                    <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", p.status === "open" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500")}>{p.status === "open" ? "Open" : "Closed"}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                    {p.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>}
                    {p.type && <span>{p.type}</span>}
                    <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{p.applicantCount} applicant{p.applicantCount === 1 ? "" : "s"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => togglePosting(p)} className={`${btn.ghost} h-8`} data-testid={`talent-posting-toggle-${p.id}`}>{p.status === "open" ? "Close" : "Reopen"}</button>
                  <button onClick={() => { setEditing(p); setDlg(true); }} className={`${btn.secondary} h-8`} data-testid={`talent-posting-edit-${p.id}`}>Edit</button>
                  <button onClick={() => removePosting(p)} aria-label="Delete opening" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`talent-posting-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Applications */}
      <div>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-navy-900"><Inbox className="h-4 w-4 text-[#C9A96E]" />Applications<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{stats.total || 0}</span></h3>
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="talent-app-filters">
          {[...STATUSES.map((s) => [s, STATUS_META[s][0]]), ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`talent-filter-${k}`} className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors", filter === k ? "bg-navy-900 text-white" : "bg-slate-100 text-slate-500 hover:text-navy-900")}>{l} ({k === "all" ? (stats.total || 0) : (stats[k] || 0)})</button>
          ))}
        </div>
        {apps === null ? (
          <Card><div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div></Card>
        ) : shown.length === 0 ? (
          <Card><Empty icon={Inbox} title="No applications" text="Applications submitted from your public Careers page will show up here." /></Card>
        ) : (
          <div className="space-y-3">{shown.map((a) => <AppCard key={a.id} a={a} onChange={loadApps} />)}</div>
        )}
      </div>

      <PostingDialog open={dlg} onOpenChange={setDlg} initial={editing} onSaved={loadPostings} />
    </div>
  );
}
